import { useState, useEffect, useCallback, useRef } from 'react';
import { authenticatedFetch, ApiError, isSessionValid, onSessionExpired } from '@/api/client';

// T014: Admin API prefix (relative to API_URL, which already includes /api)
// Backend routes define '/admin/...' paths
// Full path: {API_URL}/admin/metrics, {API_URL}/admin/users, etc.
const ADMIN_PREFIX = '/admin';

// =====================================
// Type Definitions
// =====================================

export interface AdminMetrics {
  totalUsers: number;
  usersToday: number;
  usersThisWeek: number;
  activeUsersToday: number;
  activeUsers7d: number;
  totalReplays: number;
  replaysToday: number;
  replaysThisWeek: number;
  pendingReplays: number;
  failedReplays: number;
  totalViews: number;
  totalLikes: number;
  totalComments: number;
  currentFrameworkVersion: string;
}

export interface AdminUserView {
  id: string;
  username: string;
  email: string | null;
  emailVerified: boolean;
  isAdmin: boolean;
  avatarUrl: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  replayCount: number;
  oauthProviders: string[];
}

export interface AdminUserDetail extends AdminUserView {
  recentReplays: {
    id: string;
    title: string | null;
    createdAt: string;
    status: string;
  }[];
  oauthLinks: {
    provider: string;
    providerUsername: string | null;
    createdAt: string;
  }[];
  recentComments: {
    id: string;
    content: string;
    entityType: string;
    createdAt: string;
  }[];
}

export interface AdminReplayView {
  id: string;
  title: string | null;
  originalFilename: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  visibility: 'public' | 'unlisted';
  errorMessage: string | null;
  mapName: string | null;
  team0Score: number;
  team1Score: number;
  durationSeconds: number | null;
  likeCount: number;
  viewCount: number;
  commentCount: number;
  qualityScore: number | null;
  frameworkVersion: string | null;
  replayFileKey: string | null;
  owner: { id: string; username: string } | null;
  createdAt: string;
}

export interface AdminFeedbackView {
  id: string;
  title: string;
  upvoteCount: number;
  commentCount: number;
  createdAt: string;
  author: { id: string; username: string };
  category: { id: string; name: string; color: string };
  status: { id: string; name: string; color: string };
}

export interface ActivityEvent {
  id: string;
  type: string;
  entityId: string;
  title: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AdminTokenView {
  id: string;
  name: string;
  scope: string;
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  owner: { id: string; username: string };
}

export interface AdminDeviceView {
  id: string;
  deviceName: string | null;
  clientId: string;
  status: 'active' | 'revoked';
  createdAt: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  owner: { id: string; username: string };
}

export interface AnalyticsDataPoint {
  date: string;
  value: number;
}

export interface AnalyticsSeries {
  label: string;
  data: AnalyticsDataPoint[];
  total: number;
}

export interface AnalyticsResponse {
  period: '7d' | '30d' | '90d' | 'all';
  series: {
    uploads: AnalyticsSeries;
    registrations: AnalyticsSeries;
    views: AnalyticsSeries;
    activeUsers: AnalyticsSeries;
  };
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// =====================================
// API Helper
// T014-T017: Refactored to use centralized authenticatedFetch from client.ts
// =====================================

async function adminFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    // T014: Use centralized authenticatedFetch - handles 401 retry automatically
    return await authenticatedFetch<T>(`${ADMIN_PREFIX}${endpoint}`, options);
  } catch (err) {
    // T017: Handle 403 errors with clear "Admin access required" message
    // Don't trigger logout for 403 - user is authenticated but lacks admin rights
    const apiError = err as ApiError;
    if (apiError.error === 'Forbidden' || apiError.message?.toLowerCase().includes('admin')) {
      throw new Error('Accès admin requis');
    }
    // Re-throw other errors as-is
    throw err instanceof Error ? err : new Error(apiError.message || 'Request failed');
  }
}

// =====================================
// Dashboard Hooks (US1)
// =====================================

export function useMetrics(refreshInterval?: number) {
  const [data, setData] = useState<AdminMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    // Don't fetch if session is expired
    if (!isSessionValid()) {
      stopPolling();
      return;
    }
    try {
      const metrics = await adminFetch<AdminMetrics>('/metrics');
      setData(metrics);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch metrics'));
    } finally {
      setIsLoading(false);
    }
  }, [stopPolling]);

  useEffect(() => {
    fetchMetrics();
    if (refreshInterval) {
      intervalRef.current = setInterval(fetchMetrics, refreshInterval);
      // Subscribe to session expired events to stop polling
      const unsubscribe = onSessionExpired(stopPolling);
      return () => {
        stopPolling();
        unsubscribe();
      };
    }
  }, [fetchMetrics, refreshInterval, stopPolling]);

  return { data, isLoading, error, refetch: fetchMetrics };
}

export function useRecentReplays(limit: number = 5) {
  const [data, setData] = useState<AdminReplayView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReplays = useCallback(async () => {
    try {
      const result = await adminFetch<{ replays: AdminReplayView[] }>(
        `/recent-replays?limit=${limit}`
      );
      setData(result.replays);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch replays'));
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchReplays();
  }, [fetchReplays]);

  return { data, isLoading, error, refetch: fetchReplays };
}

export function useRecentUsers(limit: number = 5) {
  const [data, setData] = useState<AdminUserView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const result = await adminFetch<{ users: AdminUserView[] }>(
        `/recent-users?limit=${limit}`
      );
      setData(result.users);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch users'));
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { data, isLoading, error, refetch: fetchUsers };
}

// =====================================
// User Management Hooks (US2)
// =====================================

export interface UseUsersOptions {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'createdAt' | 'lastLoginAt' | 'replayCount';
  sortOrder?: 'asc' | 'desc';
  isAdmin?: boolean;
}

export function useUsers(options: UseUsersOptions = {}) {
  const [data, setData] = useState<AdminUserView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.search) params.set('search', options.search);
      if (options.sortBy) params.set('sortBy', options.sortBy);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);
      if (options.isAdmin !== undefined) params.set('isAdmin', String(options.isAdmin));

      const result = await adminFetch<{ users: AdminUserView[]; pagination: Pagination }>(
        `/users?${params}`
      );
      setData(result.users);
      setPagination(result.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch users'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.search, options.sortBy, options.sortOrder, options.isAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { data, pagination, isLoading, error, refetch: fetchUsers };
}

export function useUserDetail(userId: string | null) {
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      const user = await adminFetch<AdminUserDetail>(`/users/${userId}`);
      setData(user);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user'));
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return { data, isLoading, error, refetch: fetchUser };
}

export function useToggleAdmin() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const toggleAdmin = async (userId: string, isAdmin: boolean): Promise<AdminUserView | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await adminFetch<{ success: boolean; user: AdminUserView }>(
        `/users/${userId}/admin`,
        {
          method: 'PATCH',
          body: JSON.stringify({ isAdmin }),
        }
      );
      return result.user;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to toggle admin'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { toggleAdmin, isLoading, error };
}

// =====================================
// Replay Management Hooks (US3)
// =====================================

export interface UseReplaysOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  visibility?: string;
  ownerId?: string;
  sortBy?: 'createdAt' | 'viewCount' | 'likeCount';
  sortOrder?: 'asc' | 'desc';
}

export function useReplays(options: UseReplaysOptions = {}) {
  const [data, setData] = useState<AdminReplayView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReplays = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.search) params.set('search', options.search);
      if (options.status) params.set('status', options.status);
      if (options.visibility) params.set('visibility', options.visibility);
      if (options.ownerId) params.set('ownerId', options.ownerId);
      if (options.sortBy) params.set('sortBy', options.sortBy);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);

      const result = await adminFetch<{ replays: AdminReplayView[]; pagination: Pagination }>(
        `/replays?${params}`
      );
      setData(result.replays);
      setPagination(result.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch replays'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.search, options.status, options.visibility, options.ownerId, options.sortBy, options.sortOrder]);

  useEffect(() => {
    fetchReplays();
  }, [fetchReplays]);

  return { data, pagination, isLoading, error, refetch: fetchReplays };
}

export function useDeleteReplay() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteReplay = async (replayId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/replays/${replayId}`, { method: 'DELETE' });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete replay'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteReplay, isLoading, error };
}

export function useRetryReplay() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const retryReplay = async (replayId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/replays/${replayId}/retry`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to retry replay'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { retryReplay, isLoading, error };
}

export function useForceRecompile() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const forceRecompile = async (replayId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/replays/${replayId}/force-recompile`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to force recompile'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { forceRecompile, isLoading, error };
}

export function useChangeVisibility() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const changeVisibility = async (
    replayId: string,
    visibility: 'public' | 'unlisted'
  ): Promise<AdminReplayView | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await adminFetch<{ success: boolean; replay: AdminReplayView }>(
        `/replays/${replayId}/visibility`,
        {
          method: 'PATCH',
          body: JSON.stringify({ visibility }),
        }
      );
      return result.replay;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to change visibility'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { changeVisibility, isLoading, error };
}

// =====================================
// Analytics Hooks (US4)
// =====================================

export function useAnalytics(period: '7d' | '30d' | '90d' | 'all' = '7d') {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      const analytics = await adminFetch<AnalyticsResponse>(`/analytics?period=${period}`);
      setData(analytics);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch analytics'));
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { data, isLoading, error, refetch: fetchAnalytics };
}

// =====================================
// Feedback Hooks (US5)
// =====================================

export interface UseFeedbacksOptions {
  page?: number;
  limit?: number;
  categoryId?: string;
  statusId?: string;
  sortBy?: 'createdAt' | 'upvoteCount';
  sortOrder?: 'asc' | 'desc';
}

export function useFeedbacks(options: UseFeedbacksOptions = {}) {
  const [data, setData] = useState<AdminFeedbackView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFeedbacks = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.categoryId) params.set('categoryId', options.categoryId);
      if (options.statusId) params.set('statusId', options.statusId);
      if (options.sortBy) params.set('sortBy', options.sortBy);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);

      const result = await adminFetch<{ feedbacks: AdminFeedbackView[]; pagination: Pagination }>(
        `/feedbacks?${params}`
      );
      setData(result.feedbacks);
      setPagination(result.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch feedbacks'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.categoryId, options.statusId, options.sortBy, options.sortOrder]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  return { data, pagination, isLoading, error, refetch: fetchFeedbacks };
}

export function useUpdateFeedbackStatus() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateStatus = async (feedbackId: string, statusId: string): Promise<AdminFeedbackView | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await adminFetch<{ success: boolean; feedback: AdminFeedbackView }>(
        `/feedbacks/${feedbackId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ statusId }),
        }
      );
      return result.feedback;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update status'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { updateStatus, isLoading, error };
}

// =====================================
// Activity Feed Hooks (US6)
// =====================================

export function useActivityFeed(limit: number = 50, type?: string) {
  const [data, setData] = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchActivity = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (type) params.set('type', type);

      const result = await adminFetch<{ events: ActivityEvent[] }>(`/activity?${params}`);
      setData(result.events);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch activity'));
    } finally {
      setIsLoading(false);
    }
  }, [limit, type]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  return { data, isLoading, error, refetch: fetchActivity };
}

// =====================================
// Token & Device Hooks (US8)
// =====================================

export interface UseTokensOptions {
  page?: number;
  limit?: number;
  userId?: string;
  scope?: string;
  status?: 'active' | 'revoked' | 'expired';
  sortBy?: 'createdAt' | 'lastUsedAt';
  sortOrder?: 'asc' | 'desc';
}

export function useTokens(options: UseTokensOptions = {}) {
  const [data, setData] = useState<AdminTokenView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTokens = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.userId) params.set('userId', options.userId);
      if (options.scope) params.set('scope', options.scope);
      if (options.status) params.set('status', options.status);
      if (options.sortBy) params.set('sortBy', options.sortBy);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);

      const result = await adminFetch<{ tokens: AdminTokenView[]; pagination: Pagination }>(
        `/tokens?${params}`
      );
      setData(result.tokens);
      setPagination(result.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch tokens'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.userId, options.scope, options.status, options.sortBy, options.sortOrder]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  return { data, pagination, isLoading, error, refetch: fetchTokens };
}

export function useRevokeToken() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const revokeToken = async (tokenId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/tokens/${tokenId}/revoke`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to revoke token'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { revokeToken, isLoading, error };
}

export interface UseDevicesOptions {
  page?: number;
  limit?: number;
  userId?: string;
  status?: 'active' | 'revoked';
  sortBy?: 'createdAt' | 'lastUsedAt';
  sortOrder?: 'asc' | 'desc';
}

export function useDevices(options: UseDevicesOptions = {}) {
  const [data, setData] = useState<AdminDeviceView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDevices = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.userId) params.set('userId', options.userId);
      if (options.status) params.set('status', options.status);
      if (options.sortBy) params.set('sortBy', options.sortBy);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);

      const result = await adminFetch<{ devices: AdminDeviceView[]; pagination: Pagination }>(
        `/devices?${params}`
      );
      setData(result.devices);
      setPagination(result.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch devices'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.userId, options.status, options.sortBy, options.sortOrder]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return { data, pagination, isLoading, error, refetch: fetchDevices };
}

export function useRevokeDevice() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const revokeDevice = async (deviceId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/devices/${deviceId}/revoke`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to revoke device'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { revokeDevice, isLoading, error };
}

// =====================================
// Comment Management Hooks
// =====================================

export interface AdminCommentView {
  id: string;
  content: string;
  entityType: string;
  entityId: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  author: { id: string; username: string; avatarUrl: string | null };
  replay?: { id: string; title: string | null } | null;
}

export interface UseCommentsOptions {
  page?: number;
  limit?: number;
  search?: string;
  authorId?: string;
  entityType?: string;
  isDeleted?: boolean;
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export function useAdminComments(options: UseCommentsOptions = {}) {
  const [data, setData] = useState<AdminCommentView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.search) params.set('search', options.search);
      if (options.authorId) params.set('authorId', options.authorId);
      if (options.entityType) params.set('entityType', options.entityType);
      if (options.isDeleted !== undefined) params.set('isDeleted', String(options.isDeleted));
      if (options.sortBy) params.set('sortBy', options.sortBy);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);

      const result = await adminFetch<{ comments: AdminCommentView[]; pagination: Pagination }>(
        `/comments?${params}`
      );
      setData(result.comments);
      setPagination(result.pagination);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch comments'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.search, options.authorId, options.entityType, options.isDeleted, options.sortBy, options.sortOrder]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return { data, pagination, isLoading, error, refetch: fetchComments };
}

export function useRestoreComment() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const restoreComment = async (commentId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/comments/${commentId}/restore`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to restore comment'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { restoreComment, isLoading, error };
}

export function useHardDeleteComment() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const hardDeleteComment = async (commentId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/comments/${commentId}`, { method: 'DELETE' });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete comment'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { hardDeleteComment, isLoading, error };
}

// =====================================
// Contact Messages Hooks (023-contact-form)
// =====================================

export interface ContactMessageView {
  id: string;
  name: string;
  email: string;
  subject: string;
  content: string;
  summary: string | null;
  status: 'unread' | 'read' | 'processed';
  senderIp: string;
  createdAt: string;
  readAt: string | null;
  processedAt: string | null;
  user: { id: string; username: string; email: string | null } | null;
}

export interface ContactMessagesStats {
  total: number;
  unread: number;
  read: number;
  processed: number;
  last24Hours: number;
  last7Days: number;
}

export interface UseContactMessagesOptions {
  page?: number;
  limit?: number;
  status?: 'all' | 'unread' | 'read' | 'processed';
  sortOrder?: 'asc' | 'desc';
}

export function useContactMessages(options: UseContactMessagesOptions = {}) {
  const [data, setData] = useState<ContactMessageView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (options.page) params.set('page', String(options.page));
      if (options.limit) params.set('limit', String(options.limit));
      if (options.status && options.status !== 'all') params.set('status', options.status);
      if (options.sortOrder) params.set('sortOrder', options.sortOrder);

      const result = await adminFetch<{
        messages: ContactMessageView[];
        pagination: Pagination;
        unreadCount: number;
      }>(`/contact-messages?${params}`);
      setData(result.messages);
      setPagination(result.pagination);
      setUnreadCount(result.unreadCount);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch contact messages'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.status, options.sortOrder]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return { data, pagination, unreadCount, isLoading, error, refetch: fetchMessages };
}

export function useContactMessageDetail(messageId: string | null) {
  const [data, setData] = useState<ContactMessageView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMessage = useCallback(async () => {
    if (!messageId) return;
    try {
      setIsLoading(true);
      const result = await adminFetch<{ message: ContactMessageView }>(`/contact-messages/${messageId}`);
      setData(result.message);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch message'));
    } finally {
      setIsLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    fetchMessage();
  }, [fetchMessage]);

  return { data, isLoading, error, refetch: fetchMessage };
}

export function useContactMessagesStats() {
  const [data, setData] = useState<ContactMessagesStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await adminFetch<{ stats: ContactMessagesStats }>('/contact-messages/stats');
      setData(result.stats);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch stats'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, isLoading, error, refetch: fetchStats };
}

export function useContactMessagesUnreadCount() {
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchCount = useCallback(async () => {
    // Don't fetch if session is expired
    if (!isSessionValid()) {
      stopPolling();
      return;
    }
    try {
      const result = await adminFetch<{ unreadCount: number }>('/contact-messages/unread-count');
      setCount(result.unreadCount);
    } catch {
      // Silently fail - not critical
    } finally {
      setIsLoading(false);
    }
  }, [stopPolling]);

  useEffect(() => {
    fetchCount();
    // Refresh every 30 seconds
    intervalRef.current = setInterval(fetchCount, 30000);

    // Subscribe to session expired events to stop polling
    const unsubscribe = onSessionExpired(stopPolling);

    return () => {
      stopPolling();
      unsubscribe();
    };
  }, [fetchCount, stopPolling]);

  return { count, isLoading, refetch: fetchCount };
}

export function useUpdateContactMessageStatus() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateStatus = async (
    messageId: string,
    status: 'read' | 'processed'
  ): Promise<ContactMessageView | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await adminFetch<{ message: ContactMessageView }>(
        `/contact-messages/${messageId}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        }
      );
      return result.message as ContactMessageView;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update status'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { updateStatus, isLoading, error };
}

export function useDeleteContactMessage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteMessage = async (messageId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      await adminFetch(`/contact-messages/${messageId}`, { method: 'DELETE' });
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete message'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteMessage, isLoading, error };
}

// =====================================
// Scheduled Jobs Hooks (034-nightly-replay-jobs)
// =====================================

export type JobType = 'recompilation' | 'cheat_detection' | 'stats_calculation' | 'replay_reprocessing';
export type JobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
export type JobTriggeredBy = 'scheduled' | 'manual';

export interface JobExecutionView {
  id: string;
  jobType: JobType;
  status: JobStatus;
  triggeredBy: JobTriggeredBy;
  startedAt: string;
  completedAt: string | null;
  totalItems: number | null;
  processedItems: number;
  failedItems: number;
  skippedItems: number;
  errorMessage: string | null;
  durationMs?: number;
}

export interface JobsStatusResponse {
  running: JobExecutionView[];
  lastExecutions: Record<JobType, JobExecutionView | null>;
  queueStats: {
    replayProcessing: { waiting: number; active: number; completed: number; failed: number };
    cheatDetection: { waiting: number; active: number; completed: number; failed: number };
  };
}

export interface JobsConfigResponse {
  enabled: boolean;
  schedules: {
    recompilation: string;
    cheat_detection: string;
    stats_calculation: string;
    replay_reprocessing: string;
  };
  batchSizes: {
    recompilation: number;
    cheat_detection: number;
    stats_calculation: number;
    replay_reprocessing: number;
  };
  lockTtlSeconds: number;
}

export interface UseJobsListOptions {
  page?: number;
  limit?: number;
  jobType?: JobType;
  status?: JobStatus;
}

export function useJobsStatus(refreshInterval?: number) {
  const [data, setData] = useState<JobsStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!isSessionValid()) {
      stopPolling();
      return;
    }
    try {
      // Backend returns runningJobs, we map to running for consistency
      const result = await adminFetch<{
        runningJobs: JobExecutionView[];
        lastExecutions: Record<JobType, JobExecutionView | null>;
        queueStats: JobsStatusResponse['queueStats'];
      }>('/jobs/status');
      setData({
        running: result.runningJobs,
        lastExecutions: result.lastExecutions,
        queueStats: result.queueStats,
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch jobs status'));
    } finally {
      setIsLoading(false);
    }
  }, [stopPolling]);

  useEffect(() => {
    fetchStatus();
    if (refreshInterval) {
      intervalRef.current = setInterval(fetchStatus, refreshInterval);
      const unsubscribe = onSessionExpired(stopPolling);
      return () => {
        stopPolling();
        unsubscribe();
      };
    }
  }, [fetchStatus, refreshInterval, stopPolling]);

  return { data, isLoading, error, refetch: fetchStatus };
}

export function useJobsList(options: UseJobsListOptions = {}) {
  const [data, setData] = useState<JobExecutionView[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      const limit = options.limit || 20;
      const page = options.page || 1;
      const offset = (page - 1) * limit;

      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (options.jobType) params.set('jobType', options.jobType);
      if (options.status) params.set('status', options.status);

      const result = await adminFetch<{ executions: JobExecutionView[]; total: number; limit: number; offset: number }>(
        `/jobs?${params}`
      );
      setData(result.executions);
      setPagination({
        page,
        limit: result.limit,
        total: result.total,
        totalPages: Math.ceil(result.total / result.limit),
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch jobs'));
    } finally {
      setIsLoading(false);
    }
  }, [options.page, options.limit, options.jobType, options.status]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return { data, pagination, isLoading, error, refetch: fetchJobs };
}

export function useJobDetail(executionId: string | null) {
  const [data, setData] = useState<JobExecutionView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchJob = useCallback(async () => {
    if (!executionId) return;
    try {
      setIsLoading(true);
      const result = await adminFetch<{ job: JobExecutionView }>(`/jobs/${executionId}`);
      setData(result.job);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch job'));
    } finally {
      setIsLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  return { data, isLoading, error, refetch: fetchJob };
}

export function useJobsConfig() {
  const [data, setData] = useState<JobsConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const config = await adminFetch<JobsConfigResponse>('/jobs/config');
      setData(config);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch jobs config'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { data, isLoading, error, refetch: fetchConfig };
}

export function useTriggerJob() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const triggerJob = async (jobType: JobType): Promise<{ executionId: string } | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await adminFetch<{ success: boolean; executionId: string; message: string }>(
        `/jobs/${jobType}/trigger`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      return { executionId: result.executionId };
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to trigger job'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { triggerJob, isLoading, error };
}
